// =====================================================
// PATIENT SELECT MODAL - Modal para seleccionar paciente
// Flujo: Centro actual → Empresa → Crear nuevo
// =====================================================
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search, UserPlus, Loader2, User, Building2, Link2, CheckCircle2, AlertCircle, FileText } from 'lucide-react';
import { patientsApi } from '../../api/patients';
import type { PatientSearchResult, PatientCompanySearchResult } from '../../types/patient';

interface PatientSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (patientId: string, patientName: string) => void;
}

type SearchPhase = 'idle' | 'searching-local' | 'searching-company' | 'done';

export function PatientSelectModal({ isOpen, onClose, onSelect }: PatientSelectModalProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [localResults, setLocalResults] = useState<PatientSearchResult[]>([]);
  const [companyResults, setCompanyResults] = useState<PatientCompanySearchResult[]>([]);
  const [searchPhase, setSearchPhase] = useState<SearchPhase>('idle');
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setLinkingPatientId] = useState<string | null>(null);
  
  // Estado para el formulario de vinculación
  const [patientToLink, setPatientToLink] = useState<PatientCompanySearchResult | null>(null);
  const [medicalRecordNumber, setMedicalRecordNumber] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;

    setError(null);
    setHasSearched(true);
    setLocalResults([]);
    setCompanyResults([]);
    setPatientToLink(null);

    // Fase 1: Buscar en centro actual
    setSearchPhase('searching-local');
    try {
      const localData = await patientsApi.search({ query: searchQuery.trim() });
      
      if (localData.length > 0) {
        // Encontrado en centro actual
        setLocalResults(localData);
        setSearchPhase('done');
        return;
      }

      // Fase 2: No encontrado localmente, buscar en empresa
      setSearchPhase('searching-company');
      const companyData = await patientsApi.searchCompany({ query: searchQuery.trim() });
      
      // Filtrar los que ya están vinculados y activos en el centro actual
      const notLinkedOrInactive = companyData.filter(p => !p.isActiveInCurrentSite);
      setCompanyResults(notLinkedOrInactive);
      setSearchPhase('done');

    } catch (err: any) {
      setError(err.message || 'Error al buscar pacientes');
      setSearchPhase('done');
    }
  }, [searchQuery]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !patientToLink) {
      handleSearch();
    }
  };

  const handleSelectPatient = (patient: PatientSearchResult) => {
    onSelect(patient.id, patient.fullName);
    handleClose();
  };

  const handleStartLink = (patient: PatientCompanySearchResult) => {
    setPatientToLink(patient);
    setMedicalRecordNumber('');
    setError(null);
  };

  const handleCancelLink = () => {
    setPatientToLink(null);
    setMedicalRecordNumber('');
    setError(null);
  };

  const handleConfirmLink = async () => {
    if (!patientToLink) return;

    setIsLinking(true);
    setError(null);

    try {
      await patientsApi.linkToSite({ 
        patientId: patientToLink.id,
        medicalRecordNumber: medicalRecordNumber.trim() || undefined
      });
      onSelect(patientToLink.id, patientToLink.fullName);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Error al vincular paciente');
      setIsLinking(false);
    }
  };

  const handleCreateNew = () => {
    navigate('/pacientes/nuevo?returnTo=budget');
    handleClose();
  };

  const handleClose = () => {
    setSearchQuery('');
    setLocalResults([]);
    setCompanyResults([]);
    setSearchPhase('idle');
    setHasSearched(false);
    setError(null);
    setLinkingPatientId(null);
    setPatientToLink(null);
    setMedicalRecordNumber('');
    setIsLinking(false);
    onClose();
  };

  if (!isOpen) return null;

  const isSearching = searchPhase === 'searching-local' || searchPhase === 'searching-company';
  const noResults = hasSearched && searchPhase === 'done' && localResults.length === 0 && companyResults.length === 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={handleClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {patientToLink ? 'Vincular paciente al centro' : 'Seleccionar paciente'}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            
            {/* Formulario de vinculación */}
            {patientToLink ? (
              <div className="space-y-4">
                {/* Info del paciente a vincular */}
                <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{patientToLink.fullName}</div>
                    <div className="text-sm text-gray-500">{patientToLink.documentNumber}</div>
                  </div>
                </div>

                {/* Campo número de historia */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center gap-1.5">
                      <FileText className="h-4 w-4" />
                      Nº de Historia en este centro
                    </span>
                  </label>
                  <input
                    type="text"
                    value={medicalRecordNumber}
                    onChange={(e) => setMedicalRecordNumber(e.target.value)}
                    placeholder="Ej: HC-2024-001 (opcional)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                    disabled={isLinking}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Puedes dejarlo vacío y asignarlo más tarde desde la ficha del paciente.
                  </p>
                </div>

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Botones */}
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={handleCancelLink}
                    disabled={isLinking}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  >
                    Volver
                  </button>
                  <button
                    onClick={handleConfirmLink}
                    disabled={isLinking}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {isLinking ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Vinculando...
                      </>
                    ) : (
                      <>
                        <Link2 className="h-4 w-4 mr-2" />
                        Vincular y seleccionar
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Búsqueda */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Buscar por DNI, email o teléfono
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ej: 12345678A, email@ejemplo.com..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                        disabled={isSearching}
                      />
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    </div>
                    <button
                      onClick={handleSearch}
                      disabled={!searchQuery.trim() || isSearching}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSearching ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        'Buscar'
                      )}
                    </button>
                  </div>
                </div>

                {/* Estado de búsqueda */}
                {isSearching && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {searchPhase === 'searching-local' ? (
                      <span>Buscando en el centro actual...</span>
                    ) : (
                      <span>Buscando en otros centros de la empresa...</span>
                    )}
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Resultados del centro actual */}
                {localResults.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span>
                        {localResults.length} paciente{localResults.length !== 1 ? 's' : ''} en este centro
                      </span>
                    </div>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {localResults.map((patient) => (
                        <button
                          key={patient.id}
                          onClick={() => handleSelectPatient(patient)}
                          className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
                        >
                          <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              {patient.fullName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {patient.documentNumber}
                              <span className="mx-1">·</span>
                              <span className="text-xs text-green-600">
                                En este centro
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resultados de la empresa (no vinculados al centro actual) */}
                {companyResults.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Building2 className="h-4 w-4 text-amber-500" />
                      <span>
                        {companyResults.length} paciente{companyResults.length !== 1 ? 's' : ''} en otros centros
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Estos pacientes existen en la empresa pero no están vinculados a este centro. 
                      Puedes vincularlos para crear el presupuesto.
                    </p>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {companyResults.map((patient) => (
                        <div
                          key={patient.id}
                          className="flex items-center gap-3 p-3 border border-amber-200 rounded-lg bg-amber-50"
                        >
                          <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              {patient.fullName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {patient.documentNumber}
                              {patient.linkedSites.length > 0 && (
                                <>
                                  <span className="mx-1">·</span>
                                  <span className="text-xs">
                                    En: {patient.linkedSites.map(s => s.siteName).join(', ')}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleStartLink(patient)}
                            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors"
                          >
                            <Link2 className="h-4 w-4" />
                            Vincular
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sin resultados */}
                {noResults && (
                  <div className="text-center py-6">
                    <div className="text-gray-400 mb-3">
                      <User className="h-12 w-12 mx-auto opacity-50" />
                    </div>
                    <p className="text-gray-600 font-medium mb-1">No se encontró ningún paciente</p>
                    <p className="text-sm text-gray-500 mb-4">
                      No existe ningún paciente con "{searchQuery}" en ningún centro de la empresa.
                    </p>
                    <button
                      onClick={handleCreateNew}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <UserPlus className="h-5 w-5 mr-2" />
                      Crear nuevo paciente
                    </button>
                  </div>
                )}

                {/* Mensaje inicial */}
                {!hasSearched && (
                  <div className="text-center py-6 text-gray-500">
                    <Search className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>Busca un paciente por DNI, email o teléfono</p>
                    <p className="text-sm mt-1">Se buscará primero en este centro y luego en toda la empresa</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer - solo mostrar cuando NO estamos en el formulario de vinculación */}
          {!patientToLink && (
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
              <button
                onClick={handleCreateNew}
                className="inline-flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <UserPlus className="h-5 w-5 mr-2" />
                Crear nuevo paciente
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
